import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzMessageService } from 'ng-zorro-antd/message';
import { SavedSearchService } from '../../../services/saved-search.service';
import { SaveQueryDialogComponent } from '../save-query-dialog/save-query-dialog.component';
import { ManageQueriesDialogComponent } from '../manage-queries-dialog/manage-queries-dialog.component';
import { SelectedQueryResult, UpdateSavedSearchRequest, ManageQueriesDialogResult } from '../../../interfaces/saved-search.interface';

@Component({
  selector: 'app-saved-search-buttons',
  templateUrl: './saved-search-buttons.component.html',
  styleUrls: ['./saved-search-buttons.component.less']
})
export class SavedSearchButtonsComponent implements OnInit {
  /**
   * Current search criteria from the parent form
   * Pass the form values as an object
   */
  @Input() searchCriteria: any = {};

  /**
   * Column configuration to save with the query (optional)
   */
  @Input() configuredColumns: string[] = [];

  /**
   * Dashboard ID for this screen (optional - auto-derived from route if not provided)
   */
  @Input() dashboardId?: string;

  /**
   * Whether to load default query on init
   */
  @Input() loadDefaultOnInit: boolean = false;

  /**
   * Button size: 'default' | 'small' | 'large'
   */
  @Input() buttonSize: 'default' | 'small' | 'large' = 'default';

  /**
   * Show button labels or just icons
   */
  @Input() showLabels: boolean = true;

  /**
   * Disable the save/update button when no search criteria
   */
  @Input() disableSave: boolean = false;

  /**
   * Event emitted when a saved query is applied
   * Parent component should use this to restore the form
   */
  @Output() queryApplied = new EventEmitter<any>();

  /**
   * Event emitted when default query is loaded
   */
  @Output() defaultQueryLoaded = new EventEmitter<any>();

  /**
   * Event emitted after a query is successfully saved
   */
  @Output() querySaved = new EventEmitter<void>();

  /**
   * Event emitted when the selected query is deleted
   * Parent component should use this to clear the search form
   */
  @Output() queryCleared = new EventEmitter<void>();

  /**
   * Track the currently selected/applied query for edit functionality
   */
  selectedQuery: SelectedQueryResult | null = null;
  updating = false;

  constructor(
    private modalService: NzModalService,
    private savedSearchService: SavedSearchService,
    private message: NzMessageService
  ) {}

  ngOnInit(): void {
    if (this.loadDefaultOnInit) {
      this.loadDefaultQuery();
    }
  }

  /**
   * Open Save Query dialog
   */
  saveQuery(): void {
    const modal = this.modalService.create({
      nzTitle: 'Save Query',
      nzContent: SaveQueryDialogComponent,
      nzData: {
        searchCriteria: this.searchCriteria,
        configuredColumns: this.configuredColumns,
        dashboardId: this.dashboardId
      },
      nzWidth: 400,
      nzFooter: null
    });

    modal.afterClose.subscribe((saved: boolean) => {
      if (saved) {
        this.querySaved.emit();
      }
    });
  }

  /**
   * Open My Saved Queries dialog
   */
  openSavedQueries(): void {
    const modal = this.modalService.create({
      nzTitle: 'My Saved Queries',
      nzContent: ManageQueriesDialogComponent,
      nzData: {
        dashboardId: this.dashboardId,
        selectedQueryId: this.selectedQuery?.saveSearchId
      },
      nzWidth: 700,
      nzFooter: null
    });

    modal.afterClose.subscribe((result: ManageQueriesDialogResult | null) => {
      if (result) {
        if (result.action === 'apply' && result.query) {
          this.selectedQuery = result.query;
          const criteria = result.query.searchCriteria || {};
          if (result.query.configuredColumns?.length) {
            criteria._configuredColumns = result.query.configuredColumns;
          }
          this.queryApplied.emit(criteria);
        } else if (result.action === 'deleted') {
          // Clear selected query and notify parent to reset form
          this.selectedQuery = null;
          this.queryCleared.emit();
        }
      }
    });
  }

  /**
   * Update the currently selected query with current search criteria
   */
  updateQuery(): void {
    if (!this.selectedQuery || this.disableSave || !this.selectedQuery) {
      return;
    }

    this.updating = true;
    const request: UpdateSavedSearchRequest = {
      queryName: this.selectedQuery.queryName,
      searchCriteria: this.savedSearchService.stringifySearchCriteria(this.searchCriteria),
      configuredColumns: this.configuredColumns,
      isDefault: this.selectedQuery.isDefault || false
    };

    this.savedSearchService.updateSavedSearch(
      this.selectedQuery.saveSearchId,
      request,
      this.dashboardId
    ).subscribe({
      next: () => {
        this.updating = false;
        this.message.success(`Query "${this.selectedQuery!.queryName}" updated successfully`);
        this.querySaved.emit();
      },
      error: (error) => {
        this.updating = false;
        this.message.error(error.error?.message || 'Failed to update query');
      }
    });
  }

  /**
   * Clear the selected query (used when user wants to save as new)
   */
  clearSelectedQuery(): void {
    this.selectedQuery = null;
  }

  /**
   * Load and apply default query
   * Can be called from parent component or automatically on init
   */
  loadDefaultQuery(): void {
    this.savedSearchService.getDefaultSavedSearch(this.dashboardId).subscribe({
      next: (response: any) => {
        if (response) {
          // Handle different response structures
          let query = null;

          if (response.content && response.content.searchCriteria) {
            // Nested: { content: { searchCriteria: "..." } }
            query = response.content;
          } else if (response.data && response.data.searchCriteria) {
            // Data wrapper: { data: { searchCriteria: "..." } }
            query = response.data;
          } else if (response.searchCriteria) {
            // Direct: { searchCriteria: "..." }
            query = response;
          }

          if (query && query.searchCriteria) {
            const criteria = this.savedSearchService.parseSearchCriteria(query.searchCriteria);
            // Track the selected query for edit functionality
            this.selectedQuery = {
              searchCriteria: criteria,
              configuredColumns: query.configuredColumns || [],
              queryName: query.queryName,
              saveSearchId: query.saveSearchId || query.id,
              isDefault: query.isDefault
            };
            if (query.configuredColumns?.length) {
              criteria._configuredColumns = query.configuredColumns;
            }
            this.defaultQueryLoaded.emit(criteria);
            return;
          }
        }
        // No default query found - emit empty criteria so parent can load with defaults
        this.defaultQueryLoaded.emit({});
      },
      error: () => {
        // On error, emit empty criteria so parent can still load data
        this.defaultQueryLoaded.emit({});
      }
    });
  }
}
